import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PhotoUpload from './PhotoUpload';
import DynamicForm from '../../components/DynamicForm';
import { matrimonyAPI, getMediaUrl } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [profilePic, setProfilePic] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([null, null, null, null, null, null]);
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'edit' | 'photos' | 'subscription'

  // Helper to construct full image URL cleanly
  const getImageUrl = (photoUrl) => getMediaUrl(photoUrl);

  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (user && !dataLoaded) {
      matrimonyAPI.getProfile().then(data => {
        let genderVal = data.gender || '';
        if (genderVal === 'F') genderVal = 'Female';
        if (genderVal === 'M') genderVal = 'Male';
        if (genderVal === 'O') genderVal = 'Other';

        const initializedData = {
          ...data,
          first_name: data.first_name || user.first_name || '',
          last_name: data.last_name || user.last_name || '',
          email: data.email || user.email || '',
          phone: data.phone || '',
          gender: genderVal,
          religion: data.religion || 'Hinduism',
          caste: data.caste || '',
          gotra: data.gotra || '',
          mother_tongue: data.mother_tongue || 'Nepali',
          marital_status: data.marital_status || 'Never Married',
          education: data.education || '',
          occupation: data.occupation || '',
          annual_income: data.annual_income || '',
          date_of_birth: data.date_of_birth || '',
          time_of_birth: data.time_of_birth || '',
          place_of_birth: data.place_of_birth || '',
          permanent_address: data.permanent_address || '',
          current_address: data.current_address || '',
          horoscope_preference: data.horoscope_preference || '',
          preferred_age_min: data.preferred_age_min || '',
          preferred_age_max: data.preferred_age_max || '',
          preferred_community: data.preferred_community || '',
          preferred_location: data.preferred_location || '',
          additional_preferences: data.additional_preferences || '',
          number_of_brothers: data.number_of_brothers || '',
          number_of_sisters: data.number_of_sisters || '',
        };
        setForm(initializedData);
        setDataLoaded(true);
        
        if (data.photo) {
          setProfilePic(getImageUrl(data.photo));
        }
        const galleryPhotoData = [null, null, null, null];
        for (let i = 1; i <= 4; i++) {
          const photoKey = `gallery_photo_${i}`;
          if (data[photoKey]) {
            galleryPhotoData[i - 1] = getImageUrl(data[photoKey]);
          }
        }
        setGalleryPhotos(galleryPhotoData);
      }).catch(err => {
        console.error('Failed to load profile:', err);
      });
    }
  }, [user, dataLoaded]);

  if (!user) {
    return (
      <div className="sec-wrapper" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
        <span className="badge-light">✦ Confidential Marriage Portal ✦</span>
        <h1 className="section-title">Sign In Required</h1>
        <p style={{ color: '#6E615E', margin: '0.8rem 0 2rem' }}>Please log in to view and manage your marriage profile.</p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button className="btn btn-primary" onClick={() => navigate('/consultation/login')}>Sign In to Profile →</button>
          <button className="btn btn-secondary" onClick={() => navigate('/consultation/register')}>Register Profile →</button>
        </div>
      </div>
    );
  }

  const upd = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
  };

  const save = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const formData = new FormData();
      const intFields = ['preferred_age_min', 'preferred_age_max', 'number_of_brothers', 'number_of_sisters'];
      
      Object.keys(form).forEach(key => {
        const val = form[key];
        if (key !== 'photo' && !key.startsWith('gallery_photo_') && val !== null && val !== undefined) {
          if (intFields.includes(key)) {
            if (val !== '' && !isNaN(val)) {
              formData.append(key, parseInt(val, 10));
            }
          } else {
            formData.append(key, val);
          }
        }
      });

      if (profilePic && profilePic.startsWith('data:')) {
        const blob = await fetch(profilePic).then(r => r.blob());
        formData.append('photo', blob, 'profile.jpg');
      }

      const galleryPromises = galleryPhotos.map((photo, index) => {
        return new Promise((resolve) => {
          if (photo && photo.startsWith('data:')) {
            fetch(photo).then(r => r.blob()).then(blob => {
              formData.append(`gallery_photo_${index + 1}`, blob, `gallery_${index + 1}.jpg`);
              resolve();
            });
          } else {
            resolve();
          }
        });
      });
      
      await Promise.all(galleryPromises);

      const token = localStorage.getItem('vt');
      const response = await fetch('/api/matrimony/profile/', {
        method: 'PATCH',
        headers: { 'Authorization': `Token ${token}` },
        body: formData
      });

      if (!response.ok) {
        let errDetail = 'Failed to save profile';
        try {
          const errData = await response.json();
          if (typeof errData === 'object' && errData !== null) {
            const parts = [];
            Object.entries(errData).forEach(([k, v]) => {
              parts.push(`${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
            });
            if (parts.length > 0) errDetail = parts.join(' | ');
          }
        } catch (_) {}
        throw new Error(errDetail);
      }
      
      const updatedData = await response.json();
      let genderVal = updatedData.gender || '';
      if (genderVal === 'F') genderVal = 'Female';
      if (genderVal === 'M') genderVal = 'Male';
      if (genderVal === 'O') genderVal = 'Other';

      setForm(prev => ({
        ...prev,
        ...updatedData,
        gender: genderVal,
        first_name: updatedData.first_name !== undefined ? updatedData.first_name : prev.first_name,
        last_name: updatedData.last_name !== undefined ? updatedData.last_name : prev.last_name,
        email: updatedData.email !== undefined ? updatedData.email : prev.email,
      }));

      if (refreshUser) {
        refreshUser(updatedData);
      }

      setMsg({ ok: true, text: '✓ Profile details & photos updated successfully!' });
    } catch (err) { 
      setMsg({ ok: false, text: err.message || 'Could not save profile details. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const isVerifiedMember = form.is_active_subscriber || form.is_verified;

  return (
    <div className="sec-wrapper" style={{ maxWidth: '960px' }}>
      
      {/* Profile Header Banner */}
      <div className="card" style={{ border: '1.5px solid #D4AF37', marginBottom: '2rem', padding: '2rem', background: 'linear-gradient(135deg, #FFFFFF 0%, #FAF7F2 100%)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5rem', justifyContent: 'space-between' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
            {/* Avatar / Profile Picture */}
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '84px',
                height: '84px',
                borderRadius: '50%',
                border: '3px solid #D4AF37',
                overflow: 'hidden',
                background: '#FAF6ED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2.5rem',
                boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
              }}>
                {profilePic ? (
                  <img src={profilePic} alt="Profile Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span>👤</span>
                )}
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                <h1 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.7rem', color: '#9E1B32', margin: 0 }}>
                  {form.first_name || form.last_name ? `${form.first_name} ${form.last_name}`.trim() : (user.full_name || user.username)}
                </h1>
                {form?.membership_tier === 'full' ? (
                  <span className="badge-verified" style={{ background: '#FAF6ED', color: '#8C6A23', border: '1px solid #D4AF37' }}>👑 Full Membership (Unlimited Match Search)</span>
                ) : form?.membership_tier === 'consultation' ? (
                  <span className="badge-verified">⏱️ Consultation Member (6 Months Access)</span>
                ) : (
                  <span className="badge-light" style={{ background: '#E8F5E9', color: '#2E7D32', borderColor: '#A5D6A7' }}>🎁 Free Registration Profile</span>
                )}
              </div>

              <div style={{ fontSize: '0.86rem', color: '#6E615E', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <span>📱 <strong>{form.phone || 'Phone Not Provided'}</strong></span>
                <span>•</span>
                <span>🏷️ <strong>{form.caste || 'Caste Unspecified'}</strong></span>
                <span>•</span>
                <span>📍 <strong>{form.current_address || 'Nepal'}</strong></span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/consultation/payment')}>
              💎 Membership Upgrade / QR Payment
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setActiveTab(activeTab === 'edit' ? 'details' : 'edit')}>
              {activeTab === 'edit' ? '📋 View Summary' : '✏️ Edit Profile'}
            </button>
          </div>
        </div>

        {/* Global Notification Banner */}
        {msg && (
          <div style={{
            marginTop: '1.2rem',
            padding: '0.8rem 1.2rem',
            borderRadius: '12px',
            fontSize: '0.88rem',
            fontWeight: 600,
            background: msg.ok ? '#e8f5e9' : '#FEECEC',
            color: msg.ok ? '#2e7d32' : '#9E1B32',
            border: `1px solid ${msg.ok ? 'rgba(46,125,50,0.3)' : 'rgba(158,27,50,0.3)'}`
          }}>
            {msg.text}
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #E8DFD1', paddingBottom: '0.5rem', overflowX: 'auto' }}>
        <button
          onClick={() => setActiveTab('details')}
          style={{
            padding: '0.6rem 1.3rem',
            borderRadius: '12px',
            border: 'none',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'details' ? '#9E1B32' : '#FAF7F2',
            color: activeTab === 'details' ? '#FFFFFF' : '#6E615E',
            transition: 'all 0.2s ease'
          }}
        >
          📋 Profile Summary
        </button>

        <button
          onClick={() => setActiveTab('edit')}
          style={{
            padding: '0.6rem 1.3rem',
            borderRadius: '12px',
            border: 'none',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'edit' ? '#9E1B32' : '#FAF7F2',
            color: activeTab === 'edit' ? '#FFFFFF' : '#6E615E',
            transition: 'all 0.2s ease'
          }}
        >
          ✏️ Edit Details
        </button>

        <button
          onClick={() => setActiveTab('photos')}
          style={{
            padding: '0.6rem 1.3rem',
            borderRadius: '12px',
            border: 'none',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'photos' ? '#9E1B32' : '#FAF7F2',
            color: activeTab === 'photos' ? '#FFFFFF' : '#6E615E',
            transition: 'all 0.2s ease'
          }}
        >
          📸 Upload Photos
        </button>

        <button
          onClick={() => setActiveTab('subscription')}
          style={{
            padding: '0.6rem 1.3rem',
            borderRadius: '12px',
            border: 'none',
            fontSize: '0.88rem',
            fontWeight: 700,
            cursor: 'pointer',
            background: activeTab === 'subscription' ? '#9E1B32' : '#FAF7F2',
            color: activeTab === 'subscription' ? '#FFFFFF' : '#6E615E',
            transition: 'all 0.2s ease'
          }}
        >
          💳 Subscription Status
        </button>
      </div>

      {/* TAB 1: PROFILE SUMMARY & SUBMITTED DETAILS */}
      {activeTab === 'details' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
          
          {/* Card 1: Personal & Cultural */}
          <div className="card">
            <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.2rem', color: '#9E1B32', marginBottom: '1rem', borderBottom: '1px solid #E8DFD1', pb: '0.5rem' }}>
              👤 Personal & Cultural Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', fontSize: '0.9rem' }}>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Full Name</span><strong>{form.first_name || form.last_name ? `${form.first_name} ${form.last_name}`.trim() : 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Gender</span><strong>{form.gender || 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Date of Birth</span><strong>{form.date_of_birth || 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Religion & Caste</span><strong>{form.religion} ({form.caste || 'Unspecified'})</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Gotra</span><strong>{form.gotra || 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Marital Status</span><strong>{form.marital_status}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Mother Tongue</span><strong>{form.mother_tongue}</strong></div>
            </div>
          </div>

          {/* Card 2: Contact & Address */}
          <div className="card">
            <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.2rem', color: '#9E1B32', marginBottom: '1rem', borderBottom: '1px solid #E8DFD1', pb: '0.5rem' }}>
              📱 Contact & Location
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', fontSize: '0.9rem' }}>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Phone Number</span><strong>{form.phone || 'Not Provided'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Email Address</span><strong>{form.email || user.email || 'Not Provided'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Permanent Address</span><strong>{form.permanent_address || 'Not Provided'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Current Residence</span><strong>{form.current_address || 'Not Provided'}</strong></div>
            </div>
          </div>

          {/* Card 3: Profession & Education */}
          <div className="card">
            <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.2rem', color: '#9E1B32', marginBottom: '1rem', borderBottom: '1px solid #E8DFD1', pb: '0.5rem' }}>
              🎓 Career & Education
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', fontSize: '0.9rem' }}>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Highest Education</span><strong>{form.education || 'Not Provided'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Profession / Occupation</span><strong>{form.occupation || 'Not Provided'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Annual Income</span><strong>{form.annual_income || 'Not Disclosed'}</strong></div>
            </div>
          </div>

          {/* Card 4: Horoscope & Astrological Details */}
          <div className="card">
            <h3 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.2rem', color: '#9E1B32', marginBottom: '1rem', borderBottom: '1px solid #E8DFD1', pb: '0.5rem' }}>
              🕉️ Kundali & Horoscope
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', fontSize: '0.9rem' }}>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Place of Birth</span><strong>{form.place_of_birth || 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Time of Birth</span><strong>{form.time_of_birth || 'Not Specified'}</strong></div>
              <div><span style={{ color: '#6E615E', fontSize: '0.78rem', textTransform: 'uppercase', fontWeight: 700, display: 'block' }}>Horoscope Matching Preference</span><strong>{form.horoscope_preference || 'Standard Kundali Matching'}</strong></div>
            </div>
          </div>

        </div>
      )}

      {/* TAB 2: EDIT PROFILE FORM */}
      {activeTab === 'edit' && (
        <div>
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <span className="badge-light">✦ Confidential Marriage Registry ✦</span>
            <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.8rem', color: '#9E1B32', margin: '0.3rem 0 0.2rem' }}>
              Update Profile Information
            </h2>
            <p style={{ fontSize: '0.86rem', color: '#6E615E' }}>
              Managed by Consultant <strong>Laxmikant Timilsina (9857074103)</strong>
            </p>
          </div>

          <form onSubmit={save}>
            <DynamicForm form={form} upd={upd} loading={saving} />
            <div style={{ textAlign: 'center', marginTop: '2rem', padding: '1.5rem 0' }}>
              <button className="btn btn-primary" type="submit" disabled={saving} style={{ padding: '0.9rem 2.8rem', fontSize: '1rem' }}>
                {saving ? '⏳ Saving Changes…' : 'Save Profile Changes →'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TAB 3: UPLOAD PHOTOS */}
      {activeTab === 'photos' && (
        <div className="card">
          <PhotoUpload
            profilePic={profilePic}
            setProfilePic={setProfilePic}
            galleryPhotos={galleryPhotos}
            setGalleryPhotos={setGalleryPhotos}
            onSave={save}
            saving={saving}
          />
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button className="btn btn-primary" type="button" disabled={saving} onClick={save} style={{ padding: '0.8rem 2rem' }}>
              {saving ? '⏳ Saving Photos…' : 'Save Uploaded Photos →'}
            </button>
          </div>
        </div>
      )}

      {/* TAB 4: SUBSCRIPTION STATUS */}
      {activeTab === 'subscription' && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
          {isVerifiedMember ? (
            <div>
              <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>🎉</div>
              <span className="badge-verified" style={{ marginBottom: '1rem', display: 'inline-block' }}>
                ✓ Verified Premium Subscriber
              </span>
              <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.8rem', color: '#9E1B32', marginBottom: '0.5rem' }}>
                Full Matrimonial Consultation Active
              </h2>
              <p style={{ color: '#6E615E', maxWidth: '550px', margin: '0 auto 1.5rem', fontSize: '0.92rem' }}>
                Your profile is fully verified by Consultant Laxmikant Timilsina. You have 100% access to verified match introductions and contact permissions.
              </p>
              <button className="btn btn-primary" onClick={() => navigate('/consultation')}>
                View Curated Matches →
              </button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>✨</div>
              <span className="badge" style={{ marginBottom: '1rem', background: '#e8f5e9', color: '#2e7d32' }}>
                ✓ Free Marriage Profile Active
              </span>
              <h2 style={{ fontFamily: 'var(--serif)', fontWeight: 700, fontSize: '1.8rem', color: '#9E1B32', marginBottom: '0.5rem' }}>
                Explore Your Curated Matches
              </h2>
              <p style={{ color: '#6E615E', maxWidth: '550px', margin: '0 auto 1.8rem', fontSize: '0.92rem' }}>
                Your profile is registered for <strong>100% Free</strong>. You have access to curated Nepalese matrimonial match recommendations.
              </p>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => navigate('/consultation/matches')}>
                  View Curated Matches →
                </button>
                <a
                  href="https://wa.me/9779857074103?text=Namaste%20Laxmikant%20ji,%20I%20have%20registered%20my%20Vivah%20Nepal%20profile."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  💬 WhatsApp Laxmikant Timilsina
                </a>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}